/** DOM/WebAssembly binding for generated views. Rust owns document policy, protocol validation,
 * budgets and watchdogs (`crates/flux-agent-browser`). This module only loads the cached WASM,
 * serializes browser observations and creates a browser-generated random token. Layout constants
 * and error copy are presentation. The iframe sandbox and parent CSP remain browser boundaries.
 * @module apps/web/src/lib/fluxViewBridge
 */

/** Build-injected runtime bundle hash, absent only in isolated browser fixtures. */
declare const __FLUX_VIEW_RUNTIME_VERSION__: string;
/** The only executable runtime URL embedded by the Rust document serializer. */
export const VIEW_RUNTIME_PATH =
  '/flux-views/runtime.js' +
  (typeof __FLUX_VIEW_RUNTIME_VERSION__ === 'string' && __FLUX_VIEW_RUNTIME_VERSION__
    ? `?v=${encodeURIComponent(__FLUX_VIEW_RUNTIME_VERSION__)}`
    : '');
/** Minimum inline frame height in CSS pixels. */
export const FRAME_MIN_HEIGHT = 120;
/** Maximum inline frame height in CSS pixels. */
export const FRAME_MAX_HEIGHT = 1600;

import { fluxAgentBrowserCore as core } from './fluxAgentBrowser';
export {
  loadFluxAgentBrowser as loadViewProtocol,
  fluxAgentBrowserReady as viewProtocolReady,
} from './fluxAgentBrowser';
export type { FluxAgentBrowser as ViewProtocol } from './fluxAgentBrowser';

/** Snapshot embedded once in the iframe by Rust. */
export interface ViewPayload {
  /** Per-frame capability generated by Web Crypto. */
  token: string;
  /** Parent origin from location.origin. */
  parentOrigin: string;
  /** Approved component source. */
  source: string;
  /** Approved read-only snapshot. */
  data: Record<string, unknown>;
  /** Human title. */
  title: string;
}

/** Validated Rust message returned to the UI. */
export type ViewMessage =
  | { /** Runtime loaded and ready to initialize. */ type: 'ready' }
  | { /** Approved component completed its first render. */ type: 'rendered' }
  | { /** Runtime liveness notification. */ type: 'heartbeat' }
  | {
      /** Rendered content height changed. */ type: 'resize';
      /** Requested content height in CSS pixels. */ height: number;
    }
  | {
      /** Runtime could not render the view. */ type: 'error';
      /** User-visible rendering failure. */ message: string;
    }
  | {
      /** Unsent composer suggestion from the view. */ type: 'suggest';
      /** Proposed composer text; never submitted automatically. */ text: string;
    };

/** Static presentation styles embedded in each approved view document. */
const BASE_CSS = `:root{color-scheme:light dark;--fv-bg:#ffffff;--fv-fg:#111827;--fv-muted:#6b7280;--fv-border:#e5e7eb;--fv-accent:#4f46e5;--fv-soft:#eef2ff}
@media (prefers-color-scheme:dark){:root{--fv-bg:#0b0f19;--fv-fg:#e5e7eb;--fv-muted:#9ca3af;--fv-border:#1f2937;--fv-accent:#818cf8;--fv-soft:#1e1b4b}}
*{box-sizing:border-box}html,body{margin:0;background:var(--fv-bg);color:var(--fv-fg);font:14px/1.45 system-ui,-apple-system,"Segoe UI",sans-serif}
body{padding:12px}.fv-card{border:1px solid var(--fv-border);border-radius:10px;padding:12px;margin-bottom:8px}
.fv-row{display:flex;gap:8px;align-items:center;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--fv-border)}
.fv-muted{color:var(--fv-muted);font-size:12px}.fv-button{font:inherit;border:1px solid var(--fv-border);background:var(--fv-soft);color:var(--fv-fg);border-radius:8px;padding:4px 10px;cursor:pointer}
.fv-bar{height:8px;border-radius:4px;background:var(--fv-accent)}.fv-error{color:#dc2626;white-space:pre-wrap}`;

/** Binding: serialize a snapshot with Rust's pinned-runtime CSP and HTML escaping. */
export function buildViewDocument(runtimeUrl: string, payload: ViewPayload): string {
  return core().view_document(runtimeUrl, JSON.stringify(payload), BASE_CSS);
}

/** Binding: ask Web Crypto for one 128-bit per-frame token. */
export function newViewToken(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte =>
    byte.toString(16).padStart(2, '0')
  ).join('');
}

/** Binding: pass structured-cloned data and browser event identity to Rust. */
export function parseViewMessage(
  event: Pick<MessageEvent, 'data' | 'origin' | 'source'>,
  frameWindow: unknown,
  token: string
): ViewMessage | null {
  try {
    return JSON.parse(
      core().view_message(
        JSON.stringify(event.data) ?? 'null',
        event.origin,
        !!frameWindow && event.source === frameWindow,
        token
      )
    );
  } catch {
    return null;
  }
}

/** Presentation: keep the content height inside the inline layout bounds. */
export function clampFrameHeight(height: number): number {
  return Math.min(FRAME_MAX_HEIGHT, Math.max(FRAME_MIN_HEIGHT, Math.ceil(height)));
}

/** Binding: each mounted frame receives a separate Rust budget and browser monotonic clock. */
export function createMessageBudget(now: () => number = () => performance.now()) {
  const budget = new (core().ViewBudget)();
  return { accept: () => budget.accept(now()), dispose: () => budget.free() };
}

/** Browser observations consumed by the Rust watchdog. */
export interface WatchdogState {
  /** Monotonic milliseconds now. */
  now: number;
  /** Monotonic mount time. */
  mountedAt: number;
  /** Whether a rendered event was accepted. */
  rendered: boolean;
  /** Latest heartbeat time. */
  lastHeartbeat: number | null;
  /** Number of DOM load events. */
  loads: number;
}

/** Rust stop reasons mapped to user-facing UI text. */
export type WatchdogReason = 'navigated' | 'start-timeout' | 'unresponsive' | 'flood';
/** Copy shown when a frame stops. */
export const WATCHDOG_MESSAGES: Record<WatchdogReason, string> = {
  navigated: 'This view tried to leave its frame, so it was stopped.',
  'start-timeout': 'This view did not start in time.',
  unresponsive: 'This view stopped responding, so it was closed.',
  flood: 'This view sent too many messages, so it was closed.',
};
/** Binding: serialize DOM observations and return Rust's liveness decision. */
export function watchdogVerdict(state: WatchdogState): WatchdogReason | null {
  return (core().view_watchdog(JSON.stringify(state)) || null) as WatchdogReason | null;
}
